/**
 * PackageManager — Admin web
 * GET /packages                  — list all centre packages
 * POST /packages                 — create package
 * DELETE /packages/:id           — delete unused package (in-use → 409)
 * PATCH /packages/:id/deactivate — soft deactivate
 * PATCH /packages/:id/activate   — re-activate
 * price/originalPrice stored in paise: ×100 on send, ÷100 on display.
 */
import React, { useState, useEffect } from 'react';
import { getPackages, createPackage, deletePackage, deactivatePackage, activatePackage } from '../utils/api';

function toRupees(paise) { return paise ? Math.round(paise / 100) : 0; }
function toPaise(val) {
  const n = parseFloat(String(val).replace(/[^0-9.]/g, ''));
  return isNaN(n) ? 0 : Math.round(n * 100);
}

const EMPTY_FORM = { name: '', sessions: '', price: '', originalPrice: '', validityDays: '90', condition: '', description: '' };

export default function PackageManager() {
  const [packages,    setPackages]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [listError,   setListError]   = useState('');

  const [form,          setForm]          = useState(EMPTY_FORM);
  const [creating,      setCreating]      = useState(false);
  const [createError,   setCreateError]   = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  const [togglingId,   setTogglingId]   = useState(null);
  const [deletingId,   setDeletingId]   = useState(null);
  const [deleteErrors, setDeleteErrors] = useState({});

  useEffect(() => { fetchPackages(); }, []);

  async function fetchPackages() {
    setLoading(true);
    setListError('');
    try {
      const res = await getPackages();
      setPackages(res.data.data || []);
    } catch {
      setListError('Could not load packages.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');
    if (!form.name.trim() || !form.sessions || !form.price || !form.validityDays) {
      setCreateError('Name, sessions, price, and validity are required.');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        name:          form.name.trim(),
        sessionsTotal: Number(form.sessions),
        price:         toPaise(form.price),
        validityDays:  Number(form.validityDays),
        ...(form.originalPrice.trim() ? { originalPrice: toPaise(form.originalPrice) } : {}),
        ...(form.condition.trim()     ? { condition:     form.condition.trim()        } : {}),
        ...(form.description.trim()   ? { description:   form.description.trim()      } : {}),
      };
      const res = await createPackage(payload);
      setPackages(prev => [res.data.data, ...prev]);
      setCreateSuccess(`Package "${form.name.trim()}" created.`);
      setForm(EMPTY_FORM);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Could not create package.');
    } finally {
      setCreating(false);
    }
  }

  async function handleToggle(pkg) {
    setTogglingId(pkg._id);
    try {
      if (pkg.isActive) {
        await deactivatePackage(pkg._id);
        setPackages(prev => prev.map(p => p._id === pkg._id ? { ...p, isActive: false } : p));
      } else {
        await activatePackage(pkg._id);
        setPackages(prev => prev.map(p => p._id === pkg._id ? { ...p, isActive: true } : p));
      }
    } catch {
      alert('Could not update package status. Please try again.');
    } finally {
      setTogglingId(null);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this package? This cannot be undone.')) return;
    setDeletingId(id);
    setDeleteErrors(prev => { const e = { ...prev }; delete e[id]; return e; });
    try {
      await deletePackage(id);
      setPackages(prev => prev.filter(p => p._id !== id));
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not delete package.';
      setDeleteErrors(prev => ({ ...prev, [id]: msg }));
    } finally {
      setDeletingId(null);
    }
  }

  const inputStyle = {
    border: '1px solid #ddd', borderRadius: '6px', padding: '8px 12px',
    fontSize: '14px', width: '100%', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: '12px', fontWeight: '600', color: '#555', display: 'block',
    marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px',
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto', fontFamily: "'Open Sans', sans-serif" }}>
      <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a1a', marginBottom: '24px' }}>Packages</h2>

      {/* Create form */}
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '12px', padding: '24px', marginBottom: '32px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#333', marginBottom: '20px', marginTop: 0 }}>Create Package</h3>
        <form onSubmit={handleCreate}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Name *</label>
              <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Healing Starter Pack" />
            </div>
            <div>
              <label style={labelStyle}>Sessions *</label>
              <input style={inputStyle} type="number" min="1" value={form.sessions} onChange={e => setForm(f => ({ ...f, sessions: e.target.value }))} placeholder="5" />
            </div>
            <div>
              <label style={labelStyle}>Price ₹ *</label>
              <input style={inputStyle} type="number" min="0" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="2999" />
            </div>
            <div>
              <label style={labelStyle}>Orig. Price ₹</label>
              <input style={inputStyle} type="number" min="0" value={form.originalPrice} onChange={e => setForm(f => ({ ...f, originalPrice: e.target.value }))} placeholder="3999" />
            </div>
            <div>
              <label style={labelStyle}>Validity days *</label>
              <input style={inputStyle} type="number" min="1" value={form.validityDays} onChange={e => setForm(f => ({ ...f, validityDays: e.target.value }))} placeholder="90" />
            </div>
            <div style={{ gridColumn: '1 / 3' }}>
              <label style={labelStyle}>Condition (optional)</label>
              <input style={inputStyle} value={form.condition} onChange={e => setForm(f => ({ ...f, condition: e.target.value }))} placeholder="e.g. Stress & anxiety" />
            </div>
            <div style={{ gridColumn: '3 / 6' }}>
              <label style={labelStyle}>Description (optional)</label>
              <input style={inputStyle} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What's included…" />
            </div>
          </div>
          {createError   && <p style={{ color: '#e74c3c', fontSize: '13px', margin: '8px 0' }}>{createError}</p>}
          {createSuccess && <p style={{ color: '#27ae60', fontSize: '13px', margin: '8px 0' }}>{createSuccess}</p>}
          <button
            type="submit"
            disabled={creating}
            style={{
              background: creating ? '#aaa' : '#2A9D8F', color: '#fff', border: 'none',
              borderRadius: '8px', padding: '10px 24px', fontSize: '14px',
              fontWeight: '700', cursor: creating ? 'not-allowed' : 'pointer',
            }}
          >
            {creating ? 'Creating...' : 'Create Package'}
          </button>
        </form>
      </div>

      {/* Package list */}
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#333', margin: 0 }}>All Packages</h3>
        </div>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Loading...</div>
        ) : listError ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#e74c3c' }}>{listError}</div>
        ) : packages.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#999', fontSize: '14px' }}>No packages yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                {['Name', 'Sessions', 'Price', 'Validity', 'Condition', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{
                    padding: '10px 16px', textAlign: 'left', fontWeight: '700',
                    color: '#555', fontSize: '11px', textTransform: 'uppercase',
                    letterSpacing: '0.5px', borderBottom: '1px solid #e8e8e8',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {packages.map(pkg => (
                <React.Fragment key={pkg._id}>
                  <tr style={{ borderBottom: deleteErrors[pkg._id] ? 'none' : '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px 16px', fontWeight: '700', color: '#1a1a1a' }}>{pkg.name}</td>
                    <td style={{ padding: '12px 16px', color: '#555' }}>{pkg.sessionsTotal}</td>
                    <td style={{ padding: '12px 16px', color: '#27ae60', fontWeight: '600' }}>
                      ₹{toRupees(pkg.price).toLocaleString('en-IN')}
                      {pkg.originalPrice
                        ? <span style={{ color: '#999', fontWeight: '400', marginLeft: '6px', textDecoration: 'line-through' }}>
                            ₹{toRupees(pkg.originalPrice).toLocaleString('en-IN')}
                          </span>
                        : null}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#555' }}>{pkg.validityDays}d</td>
                    <td style={{ padding: '12px 16px', color: '#888', fontSize: '12px' }}>{pkg.condition || '—'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: '12px',
                        fontSize: '11px', fontWeight: '700',
                        background: pkg.isActive ? '#e8f8f3' : '#f8f8f8',
                        color:      pkg.isActive ? '#27ae60' : '#999',
                      }}>
                        {pkg.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => handleToggle(pkg)}
                          disabled={togglingId === pkg._id || deletingId === pkg._id}
                          style={{
                            background: 'none',
                            border: `1px solid ${pkg.isActive ? '#e67e22' : '#27ae60'}`,
                            color: pkg.isActive ? '#e67e22' : '#27ae60',
                            borderRadius: '6px', padding: '4px 12px', fontSize: '12px', fontWeight: '600',
                            opacity: (togglingId === pkg._id || deletingId === pkg._id) ? 0.5 : 1,
                            cursor: (togglingId === pkg._id || deletingId === pkg._id) ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {togglingId === pkg._id ? '...' : pkg.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDelete(pkg._id)}
                          disabled={deletingId === pkg._id || togglingId === pkg._id}
                          style={{
                            background: 'none', border: '1px solid #e74c3c', color: '#e74c3c',
                            borderRadius: '6px', padding: '4px 12px', fontSize: '12px', fontWeight: '600',
                            opacity: (deletingId === pkg._id || togglingId === pkg._id) ? 0.5 : 1,
                            cursor: (deletingId === pkg._id || togglingId === pkg._id) ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {deletingId === pkg._id ? '...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {deleteErrors[pkg._id] && (
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td colSpan={7} style={{ padding: '4px 16px 10px', color: '#e74c3c', fontSize: '12px' }}>
                        {deleteErrors[pkg._id]}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
